import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import CalendarView from './CalendarView';
import AddExamModal from './AddExamModal';
import ExamDetailsModal from './ExamDetailsModal';
import ExamStatsCard from './ExamStatsCard';

const { FiPlus, FiCalendar, FiClock, FiUsers, FiCheckCircle, FiAlertCircle, FiTrendingUp, FiFilter, FiDownload, FiRefreshCw } = FiIcons;

const ExamCalendar = () => {
  const { hasPermission, currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  const [filterStatus, setFilterStatus] = useState('all'); // all, upcoming, completed, cancelled
  const [filterCourse, setFilterCourse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState(null);

  const canManageExams = hasPermission('canManageQuestions'); // Same permission as questions

  useEffect(() => {
    loadExams();
  }, [currentWorkspace?.id]);

  const loadExams = () => {
    setIsLoading(true);
    try {
      const savedExams = storage.getExams();
      setExams(savedExams);
    } catch (error) {
      console.error('Error loading exams:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddExam = (examData) => {
    try {
      const newExam = storage.addExam(examData, user?.id);
      setExams(storage.getExams());
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding exam:', error);
      alert('Error adding exam. Please try again.');
    }
  };

  const handleUpdateExam = (examId, updates) => {
    try {
      storage.updateExam(examId, updates);
      setExams(storage.getExams());
      if (selectedExam?.id === examId) {
        setSelectedExam({ ...selectedExam, ...updates });
      }
    } catch (error) {
      console.error('Error updating exam:', error);
      alert('Error updating exam. Please try again.');
    }
  };

  const handleDeleteExam = (examId) => {
    if (window.confirm('Are you sure you want to delete this exam? This action cannot be undone.')) {
      try {
        storage.deleteExam(examId);
        setExams(storage.getExams());
        setShowDetailsModal(false);
        setSelectedExam(null);
      } catch (error) {
        console.error('Error deleting exam:', error);
        alert('Error deleting exam. Please try again.');
      }
    }
  };

  const handleExamClick = (exam) => {
    setSelectedExam(exam);
    setShowDetailsModal(true);
  };

  const handleMarkComplete = (examId) => {
    const exam = exams.find(e => e.id === examId);
    if (exam) {
      const updates = {
        status: 'completed',
        completedAt: new Date().toISOString(),
        actualTakers: exam.expectedTakers - (exam.absentees || 0)
      };
      handleUpdateExam(examId, updates);
    }
  };

  const handleMarkAbsentees = (examId, absenteeCount) => {
    handleUpdateExam(examId, { 
      absentees: absenteeCount,
      actualTakers: exams.find(e => e.id === examId).expectedTakers - absenteeCount
    });
  };

  const handleScheduleExam = (date) => {
    setSelectedScheduleDate(date);
    setShowAddModal(true);
  };

  // Filter exams based on status and course
  const filteredExams = exams.filter(exam => {
    const statusMatch = filterStatus === 'all' || exam.status === filterStatus;
    const courseMatch = filterCourse === '' || exam.courseCode === filterCourse;
    return statusMatch && courseMatch;
  });

  // Get unique course codes for filter
  const courseCodes = [...new Set(exams.map(exam => exam.courseCode))];

  // Calculate statistics
  const stats = {
    total: exams.length,
    upcoming: exams.filter(e => e.status === 'scheduled' && new Date(e.date) > new Date()).length,
    today: exams.filter(e => {
      const today = new Date().toDateString();
      return new Date(e.date).toDateString() === today;
    }).length,
    completed: exams.filter(e => e.status === 'completed').length,
    totalTakers: exams.reduce((sum, e) => sum + (e.actualTakers || e.expectedTakers || 0), 0),
    avgAttendance: exams.length > 0 
      ? Math.round((exams.reduce((sum, e) => {
          const attendance = e.actualTakers || e.expectedTakers || 0;
          const expected = e.expectedTakers || 1;
          return sum + (attendance / expected * 100);
        }, 0) / exams.length))
      : 0
  };

  const exportCalendar = () => {
    const csvContent = [
      ['Date', 'Time', 'Course Code', 'Class Code', 'Exam Title', 'Expected Takers', 'Actual Takers', 'Absentees', 'Status', 'Duration'].join(','),
      ...exams.map(exam => [
        new Date(exam.date).toLocaleDateString(),
        exam.time,
        exam.courseCode,
        exam.classCode,
        exam.title,
        exam.expectedTakers,
        exam.actualTakers || 0,
        exam.absentees || 0,
        exam.status,
        `${exam.duration} minutes`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `exam_calendar_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Format a date to YYYY-MM-DD
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-8 ml-72">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading exam calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-8 ml-72">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Exam Calendar</h2>
          <p className="text-gray-600 mt-1">
            Schedule and track exam administration across courses
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={exportCalendar}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition-colors"
          >
            <SafeIcon icon={FiDownload} />
            <span>Export</span>
          </button>
          <button
            onClick={loadExams}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-700 transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} />
            <span>Refresh</span>
          </button>
          {canManageExams && (
            <motion.button
              onClick={() => setShowAddModal(true)}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-primary-700 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiPlus} />
              <span>Schedule Exam</span>
            </motion.button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <ExamStatsCard
          title="Total Exams"
          value={stats.total}
          icon={FiCalendar}
          color="bg-blue-500"
          change="+12%"
        />
        <ExamStatsCard
          title="Upcoming"
          value={stats.upcoming}
          icon={FiClock}
          color="bg-orange-500"
          change="+5%"
        />
        <ExamStatsCard
          title="Today"
          value={stats.today}
          icon={FiAlertCircle}
          color="bg-red-500"
          urgent={stats.today > 0}
        />
        <ExamStatsCard
          title="Completed"
          value={stats.completed}
          icon={FiCheckCircle}
          color="bg-green-500"
          change="+8%"
        />
        <ExamStatsCard
          title="Total Takers"
          value={stats.totalTakers}
          icon={FiUsers}
          color="bg-purple-500"
          change="+15%"
        />
        <ExamStatsCard
          title="Avg Attendance"
          value={`${stats.avgAttendance}%`}
          icon={FiTrendingUp}
          color="bg-indigo-500"
          change={stats.avgAttendance > 85 ? '+2%' : '-1%'}
        />
      </div>

      {/* Filters and View Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            {/* View Mode Selector */}
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
              {['month', 'week', 'day'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors capitalize ${
                    viewMode === mode
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            {/* Course Filter */}
            <select
              value={filterCourse}
              onChange={(e) => setFilterCourse(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            >
              <option value="">All Courses</option>
              {courseCodes.map(code => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>

            <button
              onClick={() => {
                setFilterStatus('all');
                setFilterCourse('');
              }}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            >
              <SafeIcon icon={FiFilter} className="mr-1" />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Calendar View */}
      <CalendarView
        exams={filteredExams}
        currentDate={currentDate}
        onDateChange={setCurrentDate}
        viewMode={viewMode}
        onExamClick={handleExamClick}
        onMarkComplete={canManageExams ? handleMarkComplete : null}
        onMarkAbsentees={canManageExams ? handleMarkAbsentees : null}
        onScheduleExam={canManageExams ? handleScheduleExam : null}
      />

      {/* Modals */}
      {canManageExams && (
        <>
          <AddExamModal
            isOpen={showAddModal}
            onClose={() => {
              setShowAddModal(false);
              setSelectedScheduleDate(null);
            }}
            onSubmit={handleAddExam}
            initialDate={selectedScheduleDate ? formatDateForInput(selectedScheduleDate) : undefined}
          />
          
          <ExamDetailsModal
            isOpen={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
            exam={selectedExam}
            onUpdate={handleUpdateExam}
            onDelete={handleDeleteExam}
            canManage={canManageExams}
          />
        </>
      )}
    </div>
  );
};

export default ExamCalendar;